import {
  Box,
  Card,
  Container,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { Skeleton as MuiSkeleton, type SkeletonProps } from '@mui/material';

interface AppointmentInformationSkeletonsProps {
  isInBranch: boolean;
}

// Using MUI Skeleton to avoid styling and sizing issues with SCL's Skeleton
const Skeleton: React.FC<SkeletonProps> = (props) => (
  <MuiSkeleton {...props} animation='wave' />
);

const AppointmentInformationSkeletons: React.FC<
  AppointmentInformationSkeletonsProps
> = ({ isInBranch }) => {
  return (
    <Box
      sx={{
        backgroundColor: '#F7F8FA',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
      data-testid='appointment-information-skeletons'
    >
      <Stack
        direction='column'
        spacing={2}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          py: 2,
        }}
      >
        <Skeleton
          variant='circular'
          width={180}
          height={180}
          sx={{ backgroundColor: 'grey.200' }}
        />

        <Typography
          variant='h4'
          sx={{
            fontSize: '27px',
            px: 2,
            maxWidth: '836px',
          }}
        >
          Looking forward to our {isInBranch ? 'meeting' : 'call'}!
        </Typography>

        <Container sx={{ display: 'flex', justifyContent: 'center' }}>
          <Stack
            direction='column'
            spacing={2}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              maxWidth: '512px',
              width: '100%',
              '& .MuiSkeleton-root': {
                borderRadius: '12px',
                backgroundColor: 'grey.200',
              },
            }}
          >
            <Card
              sx={{
                py: '24px',
                width: '100%',
                px: 0,
              }}
            >
              <Stack direction='column' spacing={3}>
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={2}
                  sx={{ px: 2, width: '100%' }}
                >
                  <Skeleton variant='rounded' width={52} height={44} />
                  <Skeleton variant='rounded' height={44} width={'70%'} />
                </Stack>

                <Stack
                  direction='column'
                  spacing={2}
                  sx={{
                    pl: 3,
                    pr: '18px',
                  }}
                >
                  <Stack direction='column' spacing={2} sx={{ width: '100%' }}>
                    <Skeleton
                      variant='rounded'
                      height={57}
                      sx={{
                        borderRadius: '10px',
                        backgroundColor: 'grey.200',
                        width: '95%',
                      }}
                    />
                    {isInBranch && (
                      <Skeleton variant='rounded' height={34} width={'60%'} />
                    )}

                    <Skeleton variant='rounded' height={34} width={'70%'} />
                    <Skeleton variant='rounded' width='90%' height={70} />
                  </Stack>

                  <Stack
                    direction='row'
                    spacing={2}
                    justifyContent='space-between'
                    sx={{ alignItems: 'center', width: '100%' }}
                  >
                    <Skeleton
                      variant='rounded'
                      height={40}
                      sx={{
                        flex: 1,
                      }}
                    />
                    <Skeleton
                      variant='rounded'
                      width='45%'
                      height={40}
                      sx={{
                        flex: 1,
                      }}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Card>

            <Stack
              direction='column'
              spacing={2}
              sx={{
                width: '100%',
                pt: '8px',
              }}
            >
              <Skeleton variant='rounded' width='100%' height={54} />
              <Skeleton variant='rounded' width='100%' height={54} />
            </Stack>
          </Stack>
        </Container>
      </Stack>
    </Box>
  );
};

export default AppointmentInformationSkeletons;
